/**
 * 상세 수수료율 목록 — 레거시 `detailFeeRate.js` UI·필터 포팅.
 * 사용자 단위 집계(거래처·제약사·의약품 수) + 설정 이동. 디자인은 RDS.
 */
import type { BasicFeeType } from '@/entities/fee-rate/api/fee-rate.api';
import { useDetailFeeGroups } from '@/entities/fee-rate/hooks/useFeeRates';
import { getDetailFeeGroupEmail } from '@/pages/fee-rates/detailFeeListUtils';
import { mapFeeTypeLabel } from '@/pages/fee-rates/feeRateUtils';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/shared/components/ui/radio-group';
import { FilterRow, Stack } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { detailFeeRateSettingPath } from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import { Banknote, Search, Settings } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const DETAIL_FEE_RATE_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 50,
  searchKeyword: '',
  basicFeeType: '',
  detailFeeConfiguredOnly: false,
};

/** 구간/고정 구분 태그 — RANGE=info, FIXED=warning */
const FeeTypeTag: React.FC<{ feeType?: string }> = ({ feeType }) => {
  if (feeType === 'RANGE') {
    return <Badge variant="info">구간</Badge>;
  }
  if (feeType === 'FIXED') {
    return <Badge variant="warning">고정</Badge>;
  }
  return <Badge variant="secondary">{mapFeeTypeLabel(feeType)}</Badge>;
};

export const DetailFeeRatesPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(
    DETAIL_FEE_RATE_SEARCH_DEFAULTS,
  );

  const [keywordInput, setKeywordInput] = useState(values.searchKeyword);

  useEffect(() => {
    setKeywordInput(values.searchKeyword);
  }, [values.searchKeyword]);

  const search = useMemo(
    () => ({
      searchKeyword: values.searchKeyword || undefined,
      basicFeeType: (values.basicFeeType || undefined) as
        | BasicFeeType
        | undefined,
      detailFeeConfiguredOnly: values.detailFeeConfiguredOnly || undefined,
    }),
    [values],
  );

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const { data, isLoading, isError, error } = useDetailFeeGroups(
    search,
    pageable,
  );

  useEffect(() => {
    if (isError && error) handleApiError(error, toast);
  }, [isError, error, toast]);

  const rows = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      searchKeyword: keywordInput.trim(),
      page: 1,
    });
  };

  const goToSetting = (row: (typeof rows)[number]) => {
    const email = getDetailFeeGroupEmail(row);
    navigate({
      to: detailFeeRateSettingPath(),
      search: {
        userId: String(row.userId),
        email: email === '-' ? '' : email,
        companyName: row.companyName || '',
        basicFeeType: row.basicFeeType,
      },
    } as never);
  };

  return (
    <div className="space-y-5">
      <PageHeader icon={Banknote} title="상세 수수료율 설정" />

      <FilterCard>
        <Stack as="form" gap={16} onSubmit={handleSearch}>
          <FilterRow as="div" gap={12}>
            <Field
              label="검색"
              htmlFor="detail-fee-keyword"
              className="w-[422px] max-w-full"
            >
              <Input
                id="detail-fee-keyword"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                placeholder="업체명 또는 사용자 이메일"
              />
            </Field>
            <Button type="submit" className="ruda-button">
              <Search className="mr-2 h-4 w-4" />
              검색
            </Button>
          </FilterRow>

          <Field label="수수료 유형">
            <RadioGroup
              value={values.basicFeeType || 'all'}
              onValueChange={(value) =>
                setValues({
                  basicFeeType: value === 'all' ? '' : value,
                  page: 1,
                })
              }
              className="flex flex-wrap gap-4"
            >
              {(
                [
                  { value: 'all', label: '전체' },
                  { value: 'RANGE', label: '구간 수수료' },
                  { value: 'FIXED', label: '고정 수수료' },
                ] as const
              ).map((opt) => (
                <label
                  key={opt.value}
                  htmlFor={`detail-fee-type-${opt.value}`}
                  className="flex cursor-pointer items-center gap-2 text-sm text-ruda-text-primary"
                >
                  <RadioGroupItem
                    value={opt.value}
                    id={`detail-fee-type-${opt.value}`}
                  />
                  {opt.label}
                </label>
              ))}
            </RadioGroup>
          </Field>

          <label
            htmlFor="detail-fee-configured-only"
            className="flex cursor-pointer items-center gap-2 text-sm text-ruda-text-primary"
          >
            <Checkbox
              id="detail-fee-configured-only"
              checked={values.detailFeeConfiguredOnly}
              onCheckedChange={(checked) =>
                setValues({
                  detailFeeConfiguredOnly: checked === true,
                  page: 1,
                })
              }
            />
            상세 설정 사용자
          </label>
        </Stack>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="상세 수수료율을 불러오는 중..." />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState description="조회된 상세 수수료율이 없습니다." />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table className="min-w-max">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-center">사용자 이메일</TableHead>
                    <TableHead className="text-center">업체명</TableHead>
                    <TableHead className="text-center">대표자</TableHead>
                    <TableHead className="text-center">구간/고정</TableHead>
                    <TableHead className="text-center">상세 거래처</TableHead>
                    <TableHead className="text-center">상세 제약사</TableHead>
                    <TableHead className="text-center">상세 의약품</TableHead>
                    <TableHead className="text-center">설정하기</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.userId}>
                      <TableCell className="text-center">
                        {getDetailFeeGroupEmail(row)}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.companyName || '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.ceoName || '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center">
                          <FeeTypeTag feeType={row.basicFeeType} />
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {row.detailClinicCount ?? 0}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.detailPharmaCount ?? 0}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.detailDrugCount ?? 0}
                      </TableCell>
                      <TableCell className="text-center">
                        <MenuWriteGate>
                          <Button
                            type="button"
                            size="sm"
                            className="ruda-button"
                            onClick={() => goToSetting(row)}
                          >
                            <Settings className="mr-1 h-4 w-4" />
                            설정
                          </Button>
                        </MenuWriteGate>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>
    </div>
  );
};
